import { useState, useMemo } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";
import { UPLOAD_URL } from "../apolloClient";
import "../styles/Regulatory.css";
import { 
  FiEdit2, FiTrash2, FiUpload, FiCalendar, 
  FiChevronLeft, FiChevronRight, FiX,
  FiFileText, FiImage, FiCheck, FiGrid, FiSliders,
  FiSearch, FiDownload, FiFilter, FiEye,
  FiChevronsLeft, FiChevronsRight,
} from "react-icons/fi";

// GraphQL Definitions
const GET_REGULATORY_ALL = gql`
  query {
    regulatoryAll {
      id
      title
      date
      imagePath
      shortDescription
      fullDescription
      createdAt
    }
  }
`;

const CREATE_REGULATORY = gql`
  mutation CreateRegulatory($input: RegulatoryInput!) {
    createRegulatory(input: $input) {
      id
      title
      date
      imagePath
      shortDescription
      fullDescription
      createdAt
    }
  }
`;

const UPDATE_REGULATORY = gql`
  mutation UpdateRegulatory($id: ID!, $input: RegulatoryInput!) {
    updateRegulatory(id: $id, input: $input) {
      id
      title
      date
      imagePath
      shortDescription
      fullDescription
      createdAt
    }
  }
`;

const DELETE_REGULATORY = gql`
  mutation DeleteRegulatory($id: ID!) {
    deleteRegulatory(id: $id) {
      id
    }
  }
`;

function RegulatoryPage() {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [imagePath, setImagePath] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [fullDescription, setFullDescription] = useState("");
  const [editMode, setEditMode] = useState(null);
  const [viewMode, setViewMode] = useState("grid");
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);
  
  // Filter, search and pagination states
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(8); // Increased from 6

  const { loading, error: queryError, data, refetch } = useQuery(GET_REGULATORY_ALL, {
    fetchPolicy: "network-only",
  });

  const [createRegulatory] = useMutation(CREATE_REGULATORY);
  const [updateRegulatory] = useMutation(UPDATE_REGULATORY);
  const [deleteRegulatory] = useMutation(DELETE_REGULATORY);

  const submissions = data?.regulatoryAll || [];

  // Filter submissions based on search and date filters
  const filteredSubmissions = useMemo(() => {
    return submissions.filter(submission => {
      // Text search
      const matchesSearch = !searchTerm || 
        submission.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        submission.shortDescription?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        submission.fullDescription?.toLowerCase().includes(searchTerm.toLowerCase());

      // Date filter
      let matchesDate = true;
      if (submission.date && dateFilter !== "all") {
        const submissionDate = new Date(submission.date);
        const today = new Date();
        
        switch(dateFilter) {
          case "today":
            matchesDate = submissionDate.toDateString() === today.toDateString();
            break;
          case "week":
            const weekAgo = new Date(today);
            weekAgo.setDate(today.getDate() - 7);
            matchesDate = submissionDate >= weekAgo;
            break;
          case "month":
            const monthAgo = new Date(today);
            monthAgo.setMonth(today.getMonth() - 1);
            matchesDate = submissionDate >= monthAgo;
            break;
          case "year":
            const yearAgo = new Date(today);
            yearAgo.setFullYear(today.getFullYear() - 1);
            matchesDate = submissionDate >= yearAgo;
            break;
          case "custom":
            if (customStartDate && customEndDate) {
              const start = new Date(customStartDate);
              const end = new Date(customEndDate);
              end.setHours(23, 59, 59, 999);
              matchesDate = submissionDate >= start && submissionDate <= end;
            }
            break;
          default:
            matchesDate = true;
        }
      }

      return matchesSearch && matchesDate;
    });
  }, [submissions, searchTerm, dateFilter, customStartDate, customEndDate]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredSubmissions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredSubmissions.slice(startIndex, endIndex);

  // Export to CSV function
  const exportToCSV = () => {
    if (filteredSubmissions.length === 0) {
      alert("No data to export!");
      return;
    }

    // Define CSV headers
    const headers = [
      "ID",
      "Title",
      "Date",
      "Short Description",
      "Full Description",
      "Image URL",
      "Created At"
    ];

    // Prepare data rows
    const rows = filteredSubmissions.map(sub => [
      sub.id,
      `"${(sub.title || "").replace(/"/g, '""')}"`,
      sub.date || "",
      `"${(sub.shortDescription || "").replace(/"/g, '""')}"`,
      `"${(sub.fullDescription || "").replace(/"/g, '""')}"`,
      sub.imagePath || "",
      new Date(sub.createdAt).toLocaleString()
    ]);

    // Combine headers and rows
    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.join(","))
    ].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    
    link.setAttribute("href", url);
    link.setAttribute("download", `regulatory-submissions-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = "hidden";
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Image Upload Handler
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("❌ Only images are allowed");
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      alert("❌ File too large (max 20MB)");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("folder", "regulatory");

    setIsUploading(true);
    
    try {
      const res = await fetch(UPLOAD_URL, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) throw new Error("Upload failed");
      const data = await res.json();

      if (data.success && data.url) {
        setImagePath(data.url);
      } else {
        throw new Error("Upload failed");
      }
    } catch (err) {
      console.error(err);
      alert("❌ Upload error");
    } finally {
      setIsUploading(false);
    }
  };

  // Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      alert("Title is required!");
      return;
    }

    const input = { title, date, imagePath, shortDescription, fullDescription };

    try {
      if (editMode) {
        await updateRegulatory({ variables: { id: editMode, input } });
      } else {
        await createRegulatory({ variables: { input } });
      }
      resetForm();
      refetch();
      setCurrentPage(1);
    } catch (err) {
      alert("❌ Error: " + err.message);
    }
  };

  const resetForm = () => {
    setTitle("");
    setDate("");
    setImagePath("");
    setShortDescription("");
    setFullDescription("");
    setEditMode(null);
  };

  const handleEdit = (entry) => {
    setEditMode(entry.id);
    setTitle(entry.title);
    setDate(entry.date);
    setImagePath(entry.imagePath);
    setShortDescription(entry.shortDescription);
    setFullDescription(entry.fullDescription);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this regulatory record?")) return;
    try {
      await deleteRegulatory({ variables: { id } });
      refetch();
      if (currentItems.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
      if (expandedCard === id) {
        setExpandedCard(null);
      }
    } catch (err) {
      alert("❌ Delete failed: " + err.message);
    }
  };

  // Toggle card expansion
  const toggleExpandCard = (id) => {
    setExpandedCard(expandedCard === id ? null : id);
  };

  // Slide Navigation
  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % filteredSubmissions.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + filteredSubmissions.length) % filteredSubmissions.length);
  const goToSlide = (index) => setCurrentSlide(index);

  // Pagination handlers
  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  if (loading) return <div className="loading-state"><div className="spinner"></div>Loading...</div>;
  if (queryError) return <div className="error-state">Error: {queryError.message}</div>;

  return (
    <div className="regulatory-container">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1><FiFileText /> Regulatory Submissions</h1>
          <p className="page-subtitle">
            Total: {submissions.length} records | 
            Showing: {filteredSubmissions.length} filtered | 
            Page {currentPage} of {totalPages}
          </p>
        </div>
        {submissions.length > 0 && (
          <div className="header-actions">
            <div className="view-switch">
              <button 
                className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                title="Grid View"
              >
                <FiGrid /> Grid
              </button>
              <button 
                className={`view-btn ${viewMode === 'slides' ? 'active' : ''}`}
                onClick={() => setViewMode('slides')}
                title="Slides View"
                disabled={filteredSubmissions.length === 0}
              >
                <FiSliders /> Slides
              </button>
            </div>
            <button 
              className="export-btn"
              onClick={exportToCSV}
              title="Export to CSV"
            >
              <FiDownload /> Export
            </button>
          </div>
        )}
      </div>

      {/* Search and Filter Bar */}
      <div className="filter-bar">
        <div className="search-box">
          <FiSearch />
          <input
            type="text"
            placeholder="Search by title or description..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
          {searchTerm && (
            <button className="clear-search" onClick={() => setSearchTerm("")}>
              <FiX />
            </button>
          )}
        </div>
        
        <div className="date-filters">
          <div className="filter-group">
            <FiFilter />
            <select 
              value={dateFilter} 
              onChange={(e) => {
                setDateFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="filter-select"
            >
              <option value="all">All Dates</option>
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
              <option value="year">Last Year</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>
          
          {dateFilter === "custom" && (
            <div className="custom-date-range">
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => {
                  setCustomStartDate(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Start Date"
              />
              <span>to</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => {
                  setCustomEndDate(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="End Date"
              />
            </div>
          )}
        </div>
      </div>

      {/* Main Content - Side by Side Layout */}
      <div className="side-by-side-layout">
        {/* Left Side - Form */}
        <div className="left-side">
          <div className="form-container">
            <div className="form-header">
              <h3>{editMode ? 'Edit Entry' : 'New Entry'}</h3>
              {editMode && (
                <button className="cancel-btn" onClick={resetForm}>
                  <FiX /> Cancel
                </button>
              )}
            </div>
            
            <form onSubmit={handleSubmit} className="regulatory-form">
              <div className="form-group">
                <label><FiFileText /> Title *</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter title"
                  required
                />
              </div>
              
              <div className="form-group">
                <label><FiCalendar /> Date</label>
                <input 
                  type="date" 
                  value={date} 
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label><FiImage /> Image</label>
                <div className="upload-area">
                  <label className="upload-label">
                    <FiUpload />
                    {isUploading ? 'Uploading...' : 'Choose Image'}
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleFileUpload}
                      hidden
                    />
                  </label>
                  {imagePath && (
                    <div className="upload-status">
                      <img src={imagePath} alt="Preview" />
                      <span><FiCheck /> Uploaded</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Short Description</label>
                <textarea
                  rows="2"
                  value={shortDescription}
                  onChange={(e) => setShortDescription(e.target.value)}
                  placeholder="Brief description"
                  maxLength={120}
                />
              </div>

              <div className="form-group">
                <label>Full Description</label>
                <textarea
                  rows="3"
                  value={fullDescription}
                  onChange={(e) => setFullDescription(e.target.value)}
                  placeholder="Detailed description"
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn-outline" onClick={resetForm}>
                  Clear
                </button>
                <button type="submit" className="btn-primary">
                  {editMode ? 'Update' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Right Side - Submissions */}
        <div className="right-side">
          <div className="submissions-container">
            <div className="submissions-header">
              <div className="header-left">
                <h3>All Submissions ({filteredSubmissions.length})</h3>
                {dateFilter !== "all" && (
                  <div className="active-filter">
                    <FiFilter /> Date Filter: {dateFilter === "custom" ? "Custom Range" : dateFilter}
                  </div>
                )}
              </div>
              <div className="header-right">
                <div className="pagination-info">
                  Showing {Math.min(startIndex + 1, filteredSubmissions.length)} to {Math.min(endIndex, filteredSubmissions.length)} of {filteredSubmissions.length} entries
                </div>
                <div className="items-per-page">
                  <select 
                    value={itemsPerPage} 
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    <option value="8">8 per page</option>
                    <option value="12">12 per page</option>
                    <option value="16">16 per page</option>
                    <option value="20">20 per page</option>
                  </select>
                </div>
              </div>
            </div>
            
            {viewMode === 'grid' ? (
              <>
                {filteredSubmissions.length === 0 ? (
                  <div className="empty-content">
                    <FiFileText />
                    <p>No submissions found</p>
                    {searchTerm && <p className="empty-hint">Try changing your search term</p>}
                  </div>
                ) : (
                  <>
                    <div className="cards-grid">
                      {currentItems.map((entry) => (
                        <div 
                          key={entry.id} 
                          className={`card-item ${expandedCard === entry.id ? 'expanded' : ''}`}
                        >
                          <div className="card-top">
                            {entry.imagePath && (
                              <div className="card-img">
                                <img src={entry.imagePath} alt={entry.title} />
                              </div>
                            )}
                            <div className="card-actions">
                              <button onClick={() => handleEdit(entry)} className="action-btn edit" title="Edit">
                                <FiEdit2 />
                              </button>
                              <button onClick={() => handleDelete(entry.id)} className="action-btn delete" title="Delete">
                                <FiTrash2 />
                              </button>
                            </div>
                          </div>
                          
                          <div className="card-body">
                            <div className="card-date">
                              <FiCalendar /> {entry.date ? new Date(entry.date).toLocaleDateString() : 'No date'}
                            </div>
                            <h4 className="card-title" title={entry.title}>
                              {entry.title}
                            </h4>
                            {entry.shortDescription && (
                              <p className="card-summary" title={entry.shortDescription}>
                                {entry.shortDescription}
                              </p>
                            )}
                            {entry.fullDescription && (
                              <div className={`card-description ${expandedCard === entry.id ? 'expanded' : ''}`}>
                                {expandedCard === entry.id ? entry.fullDescription : `${entry.fullDescription.substring(0, 40)}...`}
                                {!expandedCard && entry.fullDescription.length > 40 && (
                                  <button 
                                    className="read-more"
                                    onClick={() => toggleExpandCard(entry.id)}
                                  >
                                    Read more
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                          
                          <div className="card-footer">
                            <button 
                              className="view-slide-btn"
                              onClick={() => {
                                setViewMode('slides');
                                const globalIndex = filteredSubmissions.findIndex(s => s.id === entry.id);
                                setCurrentSlide(globalIndex);
                              }}
                            >
                              <FiEye /> View Details
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                      <div className="pagination-controls">
                        <div className="pagination-buttons">
                          <button 
                            className="pagination-btn first"
                            onClick={() => goToPage(1)}
                            disabled={currentPage === 1}
                          >
                            <FiChevronsLeft />
                          </button>
                          <button 
                            className="pagination-btn prev"
                            onClick={prevPage}
                            disabled={currentPage === 1}
                          >
                            <FiChevronLeft />
                          </button>
                          
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNum = totalPages - 4 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }
                            
                            return pageNum <= totalPages ? (
                              <button
                                key={pageNum}
                                className={`pagination-btn ${currentPage === pageNum ? 'active' : ''}`}
                                onClick={() => goToPage(pageNum)}
                              >
                                {pageNum}
                              </button>
                            ) : null;
                          })}
                          
                          <button 
                            className="pagination-btn next"
                            onClick={nextPage}
                            disabled={currentPage === totalPages}
                          >
                            <FiChevronRight />
                          </button>
                          <button 
                            className="pagination-btn last"
                            onClick={() => goToPage(totalPages)}
                            disabled={currentPage === totalPages}
                          >
                            <FiChevronsRight />
                          </button>
                        </div>
                        <div className="page-info">
                          Page {currentPage} of {totalPages}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              <div className="slides-view">
                <div className="slides-header">
                  <button className="back-btn" onClick={() => setViewMode('grid')}>
                    <FiChevronLeft /> Back to Grid
                  </button>
                  <div className="slide-nav-info">
                    <span className="slide-counter">
                      {currentSlide + 1} / {filteredSubmissions.length}
                    </span>
                  </div>
                </div>

                <div className="slide-container">
                  <button className="nav-btn prev" onClick={prevSlide} disabled={filteredSubmissions.length <= 1}>
                    <FiChevronLeft />
                  </button>

                  <div className="slide-content">
                    {filteredSubmissions[currentSlide] && (
                      <div className="slide-item">
                        <div className="slide-media">
                          {filteredSubmissions[currentSlide].imagePath ? (
                            <img src={filteredSubmissions[currentSlide].imagePath} alt={filteredSubmissions[currentSlide].title} />
                          ) : (
                            <div className="no-media">
                              <FiImage />
                              <p>No Image</p>
                            </div>
                          )}
                        </div>
                        
                        <div className="slide-info">
                          <h2>{filteredSubmissions[currentSlide].title}</h2>
                          <div className="slide-meta">
                            <span className="slide-date">
                              <FiCalendar /> {filteredSubmissions[currentSlide].date ? 
                                new Date(filteredSubmissions[currentSlide].date).toLocaleDateString() : 
                                'No date specified'}
                            </span>
                          </div>
                          
                          {filteredSubmissions[currentSlide].shortDescription && (
                            <div className="slide-excerpt">
                              <h4>Summary</h4>
                              <p>{filteredSubmissions[currentSlide].shortDescription}</p>
                            </div>
                          )}
                          
                          {filteredSubmissions[currentSlide].fullDescription && (
                            <div className="slide-details">
                              <h4>Description</h4>
                              <p className="slide-full-desc">{filteredSubmissions[currentSlide].fullDescription}</p>
                            </div>
                          )}
                          
                          <div className="slide-controls">
                            <button 
                              className="btn-slide edit"
                              onClick={() => {
                                handleEdit(filteredSubmissions[currentSlide]);
                                setViewMode('grid');
                              }}
                            >
                              <FiEdit2 /> Edit
                            </button>
                            <button 
                              className="btn-slide delete"
                              onClick={() => {
                                if (window.confirm('Delete this record?')) {
                                  handleDelete(filteredSubmissions[currentSlide].id);
                                  if (filteredSubmissions.length === 1) {
                                    setViewMode('grid');
                                  } else {
                                    setCurrentSlide(Math.max(0, currentSlide - 1));
                                  }
                                }
                              }}
                            >
                              <FiTrash2 /> Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <button className="nav-btn next" onClick={nextSlide} disabled={filteredSubmissions.length <= 1}>
                    <FiChevronRight />
                  </button>
                </div>

                {filteredSubmissions.length > 1 && (
                  <div className="slide-dots">
                    {filteredSubmissions.map((_, index) => (
                      <button
                        key={index}
                        className={`dot ${index === currentSlide ? 'active' : ''}`}
                        onClick={() => goToSlide(index)}
                        title={`Go to slide ${index + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default RegulatoryPage;